import { MkIcon } from "@/mk/MkIcon";
import { useRef, useState } from "react";
import type { ReactNode } from "react";

import { MkDatePicker } from "./MkDatePicker";
import { MkField } from "./MkField";

const DATE_MASK = "dd/mm/yyyy";
const DATETIME_MASK = "dd/mm/yyyy hh:mm";

export function isoToDisplay(value: string | number | null | undefined, withTime: boolean): string {
    const raw = String(value || "");
    if (!raw) {
        return "";
    }
    const [datePart = "", timePart] = raw.split(/[T ]/);
    const [year, month, day] = datePart.split("-");
    if (!year || !month || !day) {
        return raw;
    }
    const shown = `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
    if (!withTime) {
        return shown;
    }
    return timePart ? `${shown} ${timePart.slice(0, 5)}` : shown;
}

export function displayToIso(text: string, withTime: boolean): string | null {
    const raw = String(text || "").trim().replace(/^(\d{2})(\d{2})(\d{4})(?=$|\s)/, "$1/$2/$3");
    if (!raw) {
        return "";
    }
    const match = raw.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})(?:[\s,]+(\d{1,2}):(\d{2}))?$/);
    if (!match) {
        return null;
    }
    const [, day = "", month = "", year = "", hour, minute] = match;
    const dayNum = Number(day);
    const monthNum = Number(month);
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        return null;
    }
    const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    if (!withTime) {
        return iso;
    }
    const hourNum = Number(hour || 0);
    const minuteNum = Number(minute || 0);
    if (hourNum > 23 || minuteNum > 59) {
        return null;
    }
    return `${iso}T${String(hourNum).padStart(2, "0")}:${String(minuteNum).padStart(2, "0")}`;
}

interface Props {
    label?: string;
    hint?: string;
    error?: string;
    required?: boolean;
    icon?: string;
    type?: string;
    value?: string | number | null;
    placeholder?: string;
    maxLength?: number;
    disabled?: boolean;
    className?: string;
    autoFocus?: boolean;
    noPaste?: boolean;
    /** a unit written inside the box after the value, such as a currency sign or % */
    suffix?: ReactNode;
    onChange?(value: string): void;
    onEnter?(value: string): void;
}

export function MkInput({ label, hint, error, required, icon, type = "text", value = "",
                          placeholder, maxLength, disabled, className, autoFocus, noPaste,
                          suffix, onChange, onEnter }: Props) {
    const wrap = useRef<HTMLDivElement>(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [text, setText] = useState<string | null>(null);
    const [revealed, setRevealed] = useState(false);
    const isDateLike = type === "date" || type === "datetime-local";
    const isPassword = type === "password";
    const withTime = type === "datetime-local";
    const effectiveType = isPassword ? (revealed ? "text" : "password") : type;
    const current = value === null || value === undefined ? "" : String(value);

    return (
        <MkField label={label} hint={hint} error={error} required={required} className={className}>
            {isDateLike ? (
                <div className="mk-input-wrap mk-date-wrap" ref={wrap}>
                    <input className="mk-input-el mk-date-text" type="text" dir="ltr" inputMode="numeric"
                           value={text !== null ? text : isoToDisplay(current, withTime)}
                           placeholder={placeholder || (withTime ? DATETIME_MASK : DATE_MASK)}
                           disabled={disabled}
                           onClick={() => !disabled && setPickerOpen(true)}
                           onChange={(event) => {
                               const typed = event.target.value;
                               setText(typed);
                               const iso = displayToIso(typed, withTime);
                               if (iso !== null) {
                                   onChange?.(iso);
                               }
                           }}
                           onBlur={(event) => {
                               const iso = displayToIso(event.target.value, withTime);
                               setText(null);
                               if (iso === null) {
                                   onChange?.("");
                               }
                           }}
                           onKeyDown={(event) => {
                               if (event.key === "Enter") {
                                   setPickerOpen(false);
                                   onEnter?.(event.currentTarget.value);
                               } else if (event.key === "ArrowDown") {
                                   setPickerOpen(true);
                               }
                           }} />
                    <button type="button" className="mk-date-btn" tabIndex={-1} disabled={disabled}
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => !disabled && setPickerOpen((open) => !open)}>
                        <MkIcon name={withTime ? "fa-calendar-o" : "fa-calendar"} />
                    </button>
                    {pickerOpen && (
                        <MkDatePicker anchor={wrap.current} value={current} withTime={withTime}
                                      onPick={(iso) => {
                                          setText(null);
                                          onChange?.(iso);
                                      }}
                                      onClose={() => setPickerOpen(false)} />
                    )}
                </div>
            ) : (
                <div className="mk-input-wrap">
                    {icon && <MkIcon name={icon} className="mk-input-icon" />}
                    <input className="mk-input-el" type={effectiveType} value={current}
                           placeholder={placeholder} maxLength={maxLength} disabled={disabled}
                           autoFocus={autoFocus}
                           autoComplete={noPaste || isPassword ? "off" : undefined}
                           onChange={(event) => onChange?.(event.target.value)}
                           onKeyDown={(event) => {
                               if (event.key === "Enter") {
                                   onEnter?.(event.currentTarget.value);
                               }
                           }}
                           onPaste={(event) => {
                               if (noPaste) {
                                   event.preventDefault();
                               }
                           }}
                           onDrop={(event) => {
                               if (noPaste) {
                                   event.preventDefault();
                               }
                           }} />
                    {suffix !== undefined && suffix !== null && suffix !== "" && (
                        <span className="mk-input-suffix">{suffix}</span>
                    )}
                    {isPassword && (
                        <button type="button" className="mk-input-reveal" tabIndex={-1} disabled={disabled}
                                onClick={() => setRevealed((shown) => !shown)}>
                            <MkIcon name={revealed ? "fa-eye-slash" : "fa-eye"} />
                        </button>
                    )}
                </div>
            )}
        </MkField>
    );
}
